import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { DialogTrigger, Pressable } from "react-aria-components";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ChevronDown, Pause, Play, Plus, Search, TriangleAlert } from "lucide-react";
import { KitButton } from "./kit/Button";
import { cn } from "@/utils/helpers";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { usePlayback } from "@/app/routes/dashboard/components/mediaPlayback";

/**
 * The wall bar: one row of filters, shared by Trends and References.
 *
 * The two galleries sit next to each other in the nav and filter on the same
 * shape of question, so they get ONE bar rather than two that look alike. This
 * file is the whole grammar of it, and there are only three parts:
 *
 *  - A CAPTION names an axis. It is not a control and never was; it is the word
 *    that stops "Format Topic Sound Style" from being four unexplained verbs.
 *  - A RAIL is an axis with few enough options to print them all, and it holds a
 *    SET rather than a value: Format AND Topic are one question, not two turns
 *    of the same one. A picked option is FILLED in the accent, which is what
 *    makes a multi-selection countable at a glance — the picked items are the
 *    only objects in the rail, so you read how many you have set without
 *    counting words. Clicking a picked one removes it, and there is no ALL
 *    option anywhere, because an empty set already means all — a chip that says
 *    ALL is a second way to say the same thing that then has to be kept in step
 *    with the first.
 *  - A FACET is an axis with too many options to print, so it prints its current
 *    answer and opens the rest.
 *
 * NOTHING IN A POPOVER IS HOVER-ONLY. A `title` a mouse has to find is a legend
 * by another name, so an axis whose answer comes with a limit prints that limit
 * as the first line of its popover, in prose, where a phone can read it too.
 *
 * Built on DialogTrigger + KitPopover rather than KitSelect on purpose: a facet
 * has to draw its own face (caption, then value, then chevron) and KitSelect's
 * SelectValue can only print the value.
 */

/* --------------------------------------------------------------- the header */

/** The view title, with the page's actions right-aligned beside it. Wraps rather
 *  than switching: below the width where both fit, the actions drop to their own
 *  32px row under the title, which is the same arrangement without a breakpoint
 *  to keep in sync. */
export function WallTitleRow({ title, children, collapse = false }: { title: string; children?: ReactNode; collapse?: boolean }) {
  return (
    <div className="wb-titlerow">
      {collapse ? <CollapsingTitle title={title} /> : <h1 className="tt-view-title text-co-text">{title}</h1>}
      {children ? <div className="wb-acts">{children}</div> : null}
    </div>
  );
}

/** The 24px the title shrinks toward, and the scroll it takes to get there. */
const COLLAPSED_TITLE_PX = 24;
const COLLAPSE_SCROLL_PX = 40;

/**
 * Library, Foundations / Motion, "Large title" (Trends is its example): the
 * view's full Crake title at scroll 0, shrinking toward 24px over the first
 * 40px of scroll, tied to the scroll rather than timed. The bar's scrolled row
 * (WallBar `condensed`) carries the inline title once the header has gone.
 * Reduced motion keeps the title still.
 */
function CollapsingTitle({ title }: { title: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const field = useRef<HTMLElement | null>(null);
  // The cockpit's scroll field, found from the title itself (as the walls find
  // it), before useScroll subscribes; the document scrolls where there is none.
  useLayoutEffect(() => {
    field.current = ref.current?.closest<HTMLElement>(".co-grid-field") ?? null;
  }, []);
  const { scrollY } = useScroll({ container: field });
  const scale = useTransform(scrollY, (y) => {
    const node = ref.current;
    const size = node ? parseFloat(getComputedStyle(node).fontSize) || 54 : 54;
    const end = Math.min(1, COLLAPSED_TITLE_PX / size);
    return 1 - Math.min(1, Math.max(0, y / COLLAPSE_SCROLL_PX)) * (1 - end);
  });
  const reduced = useReducedMotion();
  return (
    <motion.h1 ref={ref} className="tt-view-title wb-title-collapse text-co-text" style={reduced ? undefined : { scale }}>
      {title}
    </motion.h1>
  );
}

/**
 * THE VIEW HEADER. One contract, top to bottom: title with its actions, one line
 * of subhead, a divider, and then the bar.
 *
 * The subhead slot KEEPS ITS HEIGHT when a view has nothing to say there. It is
 * a fixed min-block-size rather than a conditional render for one reason: with
 * a conditional, flipping Trends to References moved the H1 down the page,
 * because References carries a subhead and Trends did not. A view header whose
 * geometry depends on its own copy is not a contract, it is a coincidence. The
 * empty line costs one row of whitespace and buys every view the same title
 * position, the same divider Y and the same bar Y.
 *
 * The subhead is ONE LINE. It is capped at the prose measure, not at the page,
 * so a longer sentence wraps and pushes the divider down — which is the visible
 * symptom of writing two sentences where the contract asks for one.
 */
export function WallViewHeader({
  title,
  subhead,
  children,
  above,
  below,
  collapse = false,
}: {
  title: string;
  /** One line, builder-to-builder. Omitted where a view has nothing to add: the
   *  slot still occupies its row. A node rather than a string so a figure in
   *  the line can be set in ink (Signals' status sentence, Universe's counts);
   *  it is still one line of prose. */
  subhead?: ReactNode;
  /** The actions cluster: search, playback, the one primary action. */
  children?: ReactNode;
  /** A way back above the title (References: "All references" in a collection). */
  above?: ReactNode;
  /** A row under the subhead, inside the header (References: the collections). */
  below?: ReactNode;
  /** The wall's large title shrinks with the scroll ("Large title", Trends,
   *  References, Signals). */
  collapse?: boolean;
}) {
  return (
    <header className={below ? "wb-head wb-head--below" : "wb-head"}>
      {above ? <div className="wb-head-above">{above}</div> : null}
      <WallTitleRow title={title} collapse={collapse}>{children}</WallTitleRow>
      <p className="wb-sub tt-read-m">{subhead}</p>
      {below ? <div className="wb-head-below">{below}</div> : null}
    </header>
  );
}

/** The house spring for a state change that is seen: critically damped. */

/**
 * The bar's scrolled form: a second row that takes the bar's place once the
 * view header has scrolled away, and hands it back when the header returns.
 * The page owns what the row says and when it is active; the bar only makes
 * room for it and cross-fades the two.
 */
export interface WallBarCondensed {
  active: boolean;
  row: ReactNode;
}

/**
 * The bar itself.
 *
 * `axes` scrolls when it outgrows the bar and `readout` does not, because the
 * readout is the sentence saying how much of the library you are looking at and
 * it may never be the thing pushed off the end. The axes box carries its own
 * fade at the narrow step so an option past the edge reads as scrollable rather
 * than as absent — the one bug the chip groups this replaces were written to
 * prevent.
 */
export function WallBar({
  wrapAxes,
  axes,
  readout,
  condensed,
}: {
  /** Let the axes run onto a second line instead of scrolling. For a rail with
   *  more options than the view's width can hold, see `.wb-axes--wrap`. */
  wrapAxes?: boolean;
  axes: ReactNode;
  readout: ReactNode;
  /** Opt-in scrolled form. Omitted, the bar is the solid bar it always was. */
  condensed?: WallBarCondensed;
}) {
  const reduced = useReducedMotion();
  const row = (
    <div className="wb-bar">
      <div className="wb-row">
        <div className="wb-axes-box">
          <div className={wrapAxes ? "wb-axes wb-axes--wrap" : "wb-axes"}>{axes}</div>
        </div>
        <div className="wb-tail">{readout}</div>
      </div>
    </div>
  );

  if (!condensed) {
    return <div className="wb-bleed sticky top-0 z-20 border-b border-co-line bg-co-ground">{row}</div>;
  }

  /* The full bar scrolls with the page. Only the island sticks, and it
     fades in quickly once the header has gone. */
  const transition = reduced ? { duration: 0 } : { duration: 0.16, ease: [0, 0, 0.2, 1] as const };
  return (
    <div className={cn("wb-bleed", condensed.active && "wb-sticky")}>
      {condensed.active ? (
        <motion.div
          className="wb-condensed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={transition}
        >
          {condensed.row}
        </motion.div>
      ) : (
        <div className="border-b border-co-line bg-co-ground">{row}</div>
      )}
    </div>
  );
}

/**
 * The bar's floor space, for a view that has no filters.
 *
 * Same box as the bar itself, so it is the same height at every width without a
 * second number to keep in step — including the narrow step, which is a
 * container query the bar carries on its own element. No hairline and no sticky:
 * there is no bar here, only the room one would have taken, which is what makes
 * content start at the same Y on a view with filters and a view without.
 */
export function WallBarSpacer() {
  return (
    <div className="wb-bar" aria-hidden="true">
      <div className="wb-row" />
    </div>
  );
}

/** aria-hidden because the rail it labels carries the same words as its group
 *  name: read out, the caption would be a loose noun before a list that already
 *  says what it is. */
export function WallCaption({ children }: { children: ReactNode }) {
  return (
    <span className="wb-caption" aria-hidden="true">
      {children}
    </span>
  );
}

/** `strong` is the firmer line2 rule the library Filter bar draws before Sort. */
export function WallHairline({ strong = false }: { strong?: boolean }) {
  return <span className={cn("wb-hair", strong && "wb-hair--strong")} aria-hidden="true" />;
}

/* ----------------------------------------------------------------- the rail */

export interface WallRailOption<T extends string> {
  value: T;
  /** What the option prints. A platform mark may ride WITH its word and never
   *  instead of it: Web has no mark and would be a blank option otherwise. */
  label: ReactNode;
  /** The same option as plain words, for anything that has to say it out loud. */
  text: string;
}

/** Add a value or take it away: the whole of what a click on a rail means. One
 *  helper so two walls cannot disagree about it. */
export function toggleAxis<T>(set: readonly T[], value: T): T[] {
  return set.includes(value) ? set.filter((v) => v !== value) : [...set, value];
}

/**
 * An axis you can hold more than one answer on.
 *
 * TOGGLE BUTTONS, NOT A RADIO GROUP. A radio group means one-of-N and this is a
 * set, so every option is a `<button aria-pressed>` and the group is a plain
 * `role="group"`. Nothing here is a radio and nothing here has a "none" option.
 *
 * ONE TAB STOP, NOT FIVE. The options share a roving tabindex: whichever one
 * focus last sat on is the one Tab reaches, and the arrows move between them
 * inside the rail. This is also the only thing that makes a rail wide enough to
 * scroll reachable from a keyboard at all, so each move scrolls the option it
 * lands on back into the box.
 *
 * There is no clear control on the rail (user call, 12 Oct): picking an option
 * again takes it out, and the filter panel's Clear all empties every axis.
 */
export function WallRail<T extends string>({
  label,
  options,
  picked,
  onToggle,
}: {
  /** The group's accessible name — "Filter by kind". */
  label: string;
  options: readonly WallRailOption<T>[];
  /** The set. Empty means the axis narrows nothing, which is the unfiltered
   *  state and the reason there is no ALL option. */
  picked: readonly T[];
  onToggle: (value: T) => void;
}) {
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState(0);
  /* The rail is the tab stop, so exactly one option may carry tabIndex 0. If the
     option list ever shortens under a stale index, the first option takes it
     rather than nothing taking it. */
  const rovingIndex = focusIndex < options.length ? focusIndex : 0;

  const move = useCallback(
    (next: number) => {
      const count = options.length;
      if (count === 0) return;
      const index = ((next % count) + count) % count;
      setFocusIndex(index);
      const el = items.current[index];
      el?.focus();
      /* A rail can be wider than the bar and the arrows are how a keyboard
         reaches the far end of one, so the option focus lands on is pulled back
         into view rather than being focused somewhere off the edge. */
      el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    },
    [options.length],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    /* Arrows follow the writing direction, so in Arabic the left arrow still
       moves to the next option on screen rather than to the previous one. */
    const rtl =
      typeof window !== "undefined" && event.currentTarget instanceof HTMLElement
        ? window.getComputedStyle(event.currentTarget).direction === "rtl"
        : false;
    const forward = rtl ? -1 : 1;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        move(index + forward);
        break;
      case "ArrowLeft":
        event.preventDefault();
        move(index - forward);
        break;
      case "Home":
        event.preventDefault();
        move(0);
        break;
      case "End":
        event.preventDefault();
        move(options.length - 1);
        break;
      default:
        break;
    }
    /* Space and Enter are not handled: a <button> already fires its click on
       both, and re-implementing that is how one of the two stops working. */
  };

  return (
    <div role="group" aria-label={label} className="wb-rail">
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(el) => {
            items.current[index] = el;
          }}
          type="button"
          aria-pressed={picked.includes(option.value)}
          tabIndex={index === rovingIndex ? 0 : -1}
          onFocus={() => setFocusIndex(index)}
          onKeyDown={(event) => onKeyDown(event, index)}
          onClick={() => onToggle(option.value)}
          className="wb-rail-item"
        >
          {option.label}
        </button>
      ))}

    </div>
  );
}

/* ---------------------------------------------------------------- the facet */

/** A row's leading mark. lucide icons and the inline platform marks both fit. */
export type WallFacetIcon = ComponentType<{ className?: string }>;

export interface WallFacetOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Prose printed beside the row. Everything a `title` used to whisper, why a
   *  row is disabled, what a row actually narrows to, belongs here, visible. */
  note?: string;
  /** Drawn in the row's 20px icon column. */
  icon?: WallFacetIcon;
}

/**
 * An axis with too many answers to print, or one answer at a time: the face
 * prints the current answer and opens the rest in a glass popover.
 *
 * THE POPOVER (2026-09-26) is the thick glass every floating control surface
 * uses: the caption as its header, then one 40px row per answer with an icon
 * column. The picked row is marked by a 2px accent rule on its left edge and
 * ink on its title, never a check. 224px wide, or 288px when a row or the
 * caveat carries prose that needs the measure.
 */
export function WallFacet<T extends string>({
  caption,
  label,
  value,
  options,
  onChange,
  caveat,
  anyLabel,
  anyIcon,
  isDisabled = false,
  faceValue,
  restingValue,
}: {
  /** The axis name the face prints before its value, and the popover's header. */
  caption: string;
  /** The control's accessible name. */
  label: string;
  /** undefined means the axis is not narrowing anything, and the face says ANY. */
  value: T | undefined;
  options: readonly WallFacetOption<T>[];
  onChange: (next: T | undefined) => void;
  /** The limit this axis comes with, printed at the top of the popover. Omit
   *  where the axis has none; never move one into a `title`. */
  caveat?: string;
  /** The label of the row that clears the axis. Omitted where every value of the
   *  axis is a real answer and there is nothing to clear to. */
  anyLabel?: string;
  /** The clearing row's mark. */
  anyIcon?: WallFacetIcon;
  /** The axis cannot be changed right now; the face says why through its value. */
  isDisabled?: boolean;
  /** Print this on the face instead of the current answer's label, for an axis
   *  whose answer is overruled (Sort under a search reads "Best match"). */
  faceValue?: string;
  /** The answer the axis rests on when nobody has set it (Sort's Newest). The
   *  face prints it as quietly as ANY. */
  restingValue?: T;
}) {
  const [open, setOpen] = useState(false);
  const groupName = useId();
  const current = options.find((o) => o.value === value);
  const shownValue = faceValue ?? (current ? current.label : "Any");
  const prose = Boolean(caveat) || options.some((o) => o.note);

  const rows = [
    ...(anyLabel
      ? [
          {
            key: "__any",
            label: anyLabel,
            icon: anyIcon,
            selected: !current,
            disabled: false,
            note: undefined as string | undefined,
            pick: () => onChange(undefined),
          },
        ]
      : []),
    ...options.map((o) => ({
      key: o.value,
      label: o.label,
      icon: o.icon,
      selected: o.value === value,
      disabled: Boolean(o.disabled),
      note: o.note,
      pick: () => onChange(o.value),
    })),
  ];

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Pressable isDisabled={isDisabled}>
        <button
          type="button"
          className="wb-facet"
          disabled={isDisabled}
          aria-label={`${label}, currently ${shownValue}`}
        >
          <span className="wb-caption wb-caption--facet" aria-hidden="true">
            {caption}
          </span>
          {/* A brand name runs to 40 characters, so the value truncates and
              carries the whole string for a pointer. Nothing is only here: the
              popover prints every value in full. */}
          <span
            className={cn(
              "wb-facet-value",
              ((!current && !faceValue) || (!isDisabled && restingValue !== undefined && value === restingValue)) &&
                "wb-facet-value--any",
            )}
            title={shownValue}
          >
            {shownValue}
          </span>
          <ChevronDown className="wb-facet-chev" strokeWidth={2.5} aria-hidden="true" />
        </button>
      </Pressable>

      <KitPopover
        placement="bottom start"
        offset={8}
        className={cn("wb-glass-pop max-w-[calc(100vw-2rem)] p-0", prose ? "w-[288px]" : "w-[224px]")}
      >
        <p className="wb-pop-caption" aria-hidden="true">
          {caption}
        </p>
        {caveat ? <p className="tt-read-s px-3.5 pb-2.5 text-co-faint">{caveat}</p> : null}
        <div role="radiogroup" aria-label={label} className="flex flex-col">
          {rows.map((row) => (
            <label key={row.key} className="wb-opt">
              <input
                type="radio"
                name={groupName}
                className="peer sr-only"
                checked={row.selected}
                disabled={row.disabled}
                /* Focus lands on the current answer, so the list is arrow-keyed
                   from where you already are rather than from the top. */
                autoFocus={row.selected}
                onChange={() => {
                  row.pick();
                  setOpen(false);
                }}
                /* A checked radio fires no change, so picking the answer that is
                   already picked did nothing and left the popover open. The
                   click (the label forwards it, as does Space) closes it. */
                onClick={() => {
                  if (row.selected) setOpen(false);
                }}
              />
              <span className="wb-opt-row">
                <span className="wb-opt-icon" aria-hidden="true">
                  {row.icon ? <row.icon className="wb-opt-glyph" /> : null}
                </span>
                <span className="wb-opt-label">{row.label}</span>
                {row.note ? <span className="wb-opt-note">{row.note}</span> : null}
              </span>
            </label>
          ))}
        </div>
      </KitPopover>
    </DialogTrigger>
  );
}

/* -------------------------------------------------------------- the readout */

/**
 * How much of the library is on screen, and in what order.
 *
 * A count is only printed once the read has answered: "0 trends" over an
 * in-flight or failed query is a number nobody measured. Under a filter the
 * figure is the MATCH count, so it is printed against the library total rather
 * than alone — `libraryTotal` is the last total read with nothing on, and a deep
 * link that arrives already filtered has never seen one, which is the only case
 * that falls back to bare matches.
 *
 * And a figure the page knows is INCOMPLETE says so. A multi-pick is answered by
 * one request per picked value, each reading one window; if any of those windows
 * was shorter than what that value holds, or one of the reads failed, the merged
 * figure is a floor. It is then printed as "at least 8" and never against the
 * library total, because "8 of 39" reads as a settled fraction and this one is
 * not settled. The floor can still only rise as the selection widens, which is
 * what stops the count going backwards.
 */
export function WallReadout({
  isLoading,
  isError,
  filtered,
  total,
  libraryTotal,
  atLeast,
  noun,
  order,
}: {
  isLoading: boolean;
  isError: boolean;
  filtered: boolean;
  /** What the query answered with: the match count under any filter. */
  total: number;
  /** The last total read with no filter on, or null if this view has only ever
   *  been filtered. */
  libraryTotal: number | null;
  /** `total` is a FLOOR: the page read at least this many matches and knows
   *  there may be more. Suppresses the library comparison. */
  atLeast?: boolean;
  /** Singular; the plural is this plus an s. */
  noun: string;
  /**
   * How the set is ordered, when it is not the walls' default.
   *
   * Trends and References only ever sort newest first, so that was hardcoded.
   * `null` drops the clause entirely, which is the right answer on a surface
   * where the order is not a choice anyone made: a calendar is in day order the
   * way a page is in reading order, and "12 posts · by day" spends a line
   * telling you something the grid in front of you already said.
   */
  order?: { word: string; qualifier?: string } | null;
}) {
  if (isLoading) return <span className="wb-read">reading the library</span>;
  if (isError) return <span className="wb-read">count unknown</span>;

  /* The noun and the word FIRST are the two things a narrow bar can drop without
     dropping the claim: "12 · newest" still says how many, and in what order. */
  const orderNode =
    order === null ? null : (
      <>
        {" · "}
        {order?.word ?? "newest"}
        {(order ? order.qualifier : " first") && (
          <span className="wb-read-wide">{order ? order.qualifier : " first"}</span>
        )}
      </>
    );

  if (!filtered) {
    return (
      <span className="wb-read">
        <span className="wb-read-fig">{total}</span>
        <span className="wb-read-wide"> {total === 1 ? noun : `${noun}s`}</span>
        {orderNode}
      </span>
    );
  }

  /* Before the library comparison, because a floor may never be printed as a
     fraction of anything: "at least 8 of 39" would still read as a measured 8. */
  if (atLeast) {
    return (
      <span className="wb-read">
        at least <span className="wb-read-fig">{total}</span>
        <span className="wb-read-wide"> {total === 1 ? "match" : "matches"}</span>
        {orderNode}
      </span>
    );
  }

  if (libraryTotal !== null) {
    return (
      <span className="wb-read">
        <span className="wb-read-fig">{total}</span> of{" "}
        <span className="wb-read-fig">{libraryTotal}</span>
        {orderNode}
      </span>
    );
  }

  return (
    <span className="wb-read">
      <span className="wb-read-fig">{total}</span> {total === 1 ? "match" : "matches"}
      {orderNode}
    </span>
  );
}

/* --------------------------------------------------------- the partial wall */

/** The sentence a partial wall says, in one place, so the desktop notice and the
 *  phone feed's compact one can never word the same state two ways. */
export function incompleteNoticeText(failed: number, requested: number): string {
  return `${failed === 1 ? "One" : failed} of the ${requested} reads behind this view did not answer, so rows that match are missing from this wall and the count is a floor rather than a total.`;
}

/**
 * The wall is real, and it is missing rows.
 *
 * A multi-pick is answered by one request per picked value. All of them failing
 * is the error state the page already has; SOME of them failing is a different
 * thing entirely — there are rows to draw, and drawing them silently would be a
 * wall that looks complete and is not. That is the one failure mode a merged
 * wall introduces, so it gets said in words, above the wall, where the reader
 * decides what to trust rather than finding out later.
 *
 * Not a toast and not a `title`: it stays on screen for as long as the state is
 * true, and it is in the flow so it cannot be missed or dismissed into nothing.
 */
export function WallIncompleteNotice({
  failed,
  requested,
  onRetry,
  ease,
}: {
  /** Constituent reads that failed while others answered. */
  failed: number;
  /** How many reads this wall state issued in total. */
  requested: number;
  onRetry: () => void;
  /** The wall's own easing class, so this reads as part of that wall rather than
   *  as a foreign control with a different curve. */
  ease?: string;
}) {
  /* Drawn as the library's Status banner (style B): a tonal band at radius
     16, the attention mark in its well, the sentence as the title and Try
     again as the one Secondary. */
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-panel bg-co-tonal px-5 py-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-co-tonal" aria-hidden="true">
        <TriangleAlert className="size-[17px] text-co-attention" strokeWidth={1.8} />
      </span>
      <p className="min-w-[24ch] flex-1 text-[15px] font-medium leading-[1.45] text-co-text">
        {incompleteNoticeText(failed, requested)}
      </p>
      <KitButton variant="secondary" onClick={onRetry} className={cn("shrink-0", ease)}>
        Try again
      </KitButton>
    </div>
  );
}

/* ------------------------------------------------------------ the announcer */

/** Long enough that toggling four options in a row is announced once, short
 *  enough that a single toggle still reads as a response to the click. */
const ANNOUNCE_DELAY_MS = 500;

/**
 * What the wall says out loud when the filter set changes.
 *
 * ONE live region per view, and this is it. A rail now takes several answers, so
 * a viewer picks two or three in a second; an undebounced region reads the count
 * after every one of them and a screen reader spends longer catching up than the
 * person spent filtering. The delay collapses a burst into the sentence that was
 * true at the end of it.
 *
 * aria-atomic because the sentence only means anything whole: read the changed
 * word alone and "7" arrives with nothing to be seven of.
 */
export function WallFilterAnnouncer({ message }: { message: string }) {
  const [spoken, setSpoken] = useState(message);

  useEffect(() => {
    const timer = setTimeout(() => setSpoken(message), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [message]);

  return (
    <p className="sr-only" aria-live="polite" aria-atomic="true">
      {spoken}
    </p>
  );
}

/* -------------------------------------------------------- the title actions */

/** A rule and a glyph, no box. The bar below is already a field of hairlines and
 *  a bordered search box in the same eyeline reads as a fifth control. */
export function WallSearch({
  value,
  onChange,
  onSubmit,
  label,
  trailing,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  /** The field's accessible name — "Search trends", not "Search". */
  label: string;
  /** Anything the view hangs off the end of its own field: a minimum-length
   *  hint, the way to clear a submitted search. */
  trailing?: ReactNode;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="wb-search"
    >
      <Search className="wb-search-glyph" aria-hidden="true" />
      {/* The typed query is NOT uppercased: the field would then show something
          other than the string being searched for. */}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        placeholder="SEARCH"
        className="wb-search-input"
      />
      {trailing}
    </form>
  );
}

/**
 * The wall's motion switch, one glyph.
 *
 * When the system asks for reduced motion the plates are held still and the
 * glyph is disabled rather than replaced, because the system setting is a floor:
 * there is nothing here that can start playback against it, and a control that
 * disappears at a system setting is a control nobody can find again.
 */
export function WallPlayback() {
  const { reduced, paused, togglePaused } = usePlayback();
  const heldStill = "Your system asks for reduced motion, so no plate on this wall plays.";

  if (reduced) {
    return (
      <button type="button" disabled title={heldStill} className="wb-glyph">
        <Pause className="size-3.5" aria-hidden="true" />
        <span className="sr-only">{heldStill}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={togglePaused}
      aria-label={paused ? "Play plates" : "Pause plates"}
      className="wb-glyph"
    >
      {paused ? (
        <Play className="size-3.5" aria-hidden="true" />
      ) : (
        <Pause className="size-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

/** The one filled control on the page, which is how it stays the only thing that
 *  reads as an instruction rather than as a state. */
export function WallSaveButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="wb-save">
      <Plus className="size-3.5" strokeWidth={2.5} aria-hidden="true" />
      {children}
    </button>
  );
}

/**
 * The wall's pager (library References / Pager, style B): Prev and Next as
 * capsules of control glass, and the page between them in mono. The wall pages
 * instead of scrolling forever; at the end both buttons rest.
 */
export function WallPager({
  page,
  pageCount,
  onPrev,
  onNext,
}: {
  page: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  /* Clamped: a background re-read can shrink the rows under the page being
     shown, and "Page 3 of 2" is not a place. */
  const shown = Math.min(page, pageCount);
  return (
    <nav aria-label="Pages" className="wb-pager">
      <button type="button" className="wb-pager-step" disabled={page <= 1} onClick={onPrev}>
        Prev
      </button>
      <span className="wb-pager-page">
        <span aria-hidden="true">
          Page {shown} / {pageCount}
        </span>
        <span className="sr-only">
          Page {shown} of {pageCount}
        </span>
      </span>
      <button type="button" className="wb-pager-step" disabled={page >= pageCount} onClick={onNext}>
        Next
      </button>
    </nav>
  );
}
